import React, {
  useState, useEffect, useCallback, useRef,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Box from '@mui/material/Box';
import { ThemeProvider, Theme, StyledEngineProvider } from '@mui/material/styles';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { ButtonApp } from 'src/components/Button';
import {
  ListOfKeysFilterAllData,
} from 'src/typings/filter';
import { getNetHuntDeal } from 'src/store/crosstable/selectors';
import { Actions } from 'src/store/requests/filter/actions';
import { Actions as ActionsView2d } from 'src/store/view-2d/actions';
import { Actions as ActionsFilteredData } from 'src/store/filter/filtered-data/actions';
import { Actions as SpinnerActions } from 'src/store/requests/spinner-views/actions';
import { Actions as AppActions } from 'src/store/app/actions';
import { getCrmData, getBindingAppartment, getCurrentLang } from 'src/store/app/selectors';
import { useGetAuthData } from 'src/hooks';
import { getPlacement, patchNetHuntDeal } from 'src/api';
import { INetHuntDeal } from 'src/store/crosstable/reducer';
import { useConfirm } from 'material-ui-confirm';
import { IApartmentConnectDealWithPlacement } from './index';
import { dealsTheme, useStyles } from './styles';
import { translations } from '../../constants/translations';

export const NetHuntDeal: React.FC<IApartmentConnectDealWithPlacement> = React.memo(({
  placeInfo,
  isSmall,
}) => {
  const confirm = useConfirm();
  const styles = useStyles({});
  const dispatch = useDispatch();
  const crmData = useSelector(getCrmData);
  const netHuntDeal = useSelector(getNetHuntDeal);
  const currentLang = useSelector(getCurrentLang);
  const isBindingAppartment = useSelector(getBindingAppartment);
  const { getJWT } = useGetAuthData();
  const jwt = getJWT();
  const [currentDeals, setCurrentDeals] = useState<INetHuntDeal[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSelected, setIsSelected] = useState(false);

  useEffect(() => {
    if (placeInfo[ListOfKeysFilterAllData.netHuntOpportunities]) {
      setCurrentDeals(placeInfo[ListOfKeysFilterAllData.netHuntOpportunities]);
    } else {
      setCurrentDeals([]);
    }
    setIsLoading(false);
  }, [placeInfo]);

  useEffect(() => {
    let dealIsSelected = false;
    if (currentDeals) {
      currentDeals.forEach((item) => {
        if (item.id == crmData?.dealId) {
          dealIsSelected = true;
        }
      });
    }
    setIsSelected(dealIsSelected);
  }, [currentDeals, crmData]);

  if (!crmData || !netHuntDeal) {
    return null;
  }

  const {
    domain, crm,
  } = crmData;

  const { externalName, id } = netHuntDeal;

  const sendPlacementData = useCallback(async (deals: INetHuntDeal[], isRemove: boolean, selectedDeal: INetHuntDeal) => {
    setIsLoading(true);

    const { data: deal } = await patchNetHuntDeal(
      jwt,
      selectedDeal.id,
      {
        placements: isRemove ? [] : [`/api/placements/${placeInfo[ListOfKeysFilterAllData.id]}`],
      },
    );

    const { data: newPlacementData } = await getPlacement(
      jwt,
      placeInfo[ListOfKeysFilterAllData.id],
    );

    dispatch(ActionsView2d.setIsRerender());
    if (!isRemove) {
      dispatch(AppActions.setBindingAppartment(true));
      deals.push(deal);
    } else {
      dispatch(AppActions.setBindingAppartment(false));
    }
    dispatch(Actions.updatePlacement({
      data: newPlacementData,
      lang: currentLang,
    }));
    dispatch(ActionsFilteredData.updatePlacement({
      data: newPlacementData,
      lang: currentLang,
    }));
    dispatch(ActionsView2d.updatePlacement({
      data: newPlacementData,
      lang: currentLang,
    }));
    dispatch(SpinnerActions.spinnerDataEditPlacement({
      id: placeInfo[ListOfKeysFilterAllData.id],
      crm,
      data: newPlacementData,
      lang: currentLang,
    }));
    setCurrentDeals(deals);
    setIsLoading(false);
  }, [crmData, placeInfo, jwt, currentDeals]);

  const handleAddDeal = (selectedDeal: INetHuntDeal) => {
    sendPlacementData(currentDeals, false, selectedDeal);
  };

  const handleRemoveDeal = (selectedDeal: any = 0) => {
    const current = selectedDeal;
    const filtered: INetHuntDeal[] = currentDeals.reduce((acc: INetHuntDeal[], item: INetHuntDeal) => {
      if (item.id !== current.id) {
        acc.push(item);
      }
      return acc;
    }, []);

    sendPlacementData(filtered, true, current);
  };

  const handleConnectBtn = () => {
    // e.preventDefault();
    // e.stopPropagation();
    if (isSelected) {
      handleRemoveDeal(netHuntDeal);
    } else {
      handleAddDeal(netHuntDeal);
    }
  };

  const handleUnbindDeal = useCallback((e, item) => {
    e.preventDefault();
    e.stopPropagation();
    confirm({
      title: `${translations.dialogTitle} №${placeInfo[ListOfKeysFilterAllData.placementNumeration]}`,
      description: `${translations.dialogContent}: ${item.responsibleUser.username}`,
    }).then(() => {
      handleRemoveDeal(item);
    });
  }, [placeInfo]);

  const getBtnText = () => {
    if (!isSelected && !isLoading && isBindingAppartment) return translations.disabledAttach;
    if (isSelected && !isLoading) return `${translations.detachUnitFromDeal} ${externalName}`;
    if (isSelected && isLoading) return `${translations.deleting}...`;
    if (!isSelected && !isLoading) return `${translations.attachUnitToDeal} ${externalName}`;
    if (!isSelected && isLoading) return `${translations.updating}...`;
    return '';
  };

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={dealsTheme}>
        <Box key={placeInfo[ListOfKeysFilterAllData.id]} mt={isSmall ? 1 : 0}>
          { (Number(placeInfo[ListOfKeysFilterAllData.placementCanBeSet])
            || (isSelected && !Number(placeInfo[ListOfKeysFilterAllData.placementCanBeSet]))) && (
              <Box mb={currentDeals.length && !(currentDeals.length === 1 && isSelected) ? 6 : 0}>
                <ButtonApp
                  text={getBtnText()}
                  type="button"
                  color={isSelected ? 'secondary' : 'primary'}
                  onClick={handleConnectBtn}
                  isCapitalize
                  isFullSize
                  disabled={isLoading || (isBindingAppartment && !isSelected)}
                  customClassName={isSmall ? styles.isSmallBtn : ''}
                  variant={isSelected ? 'outlined' : 'contained'}
                />
              </Box>
          )}
          {currentDeals && currentDeals.length && !(currentDeals.length === 1 && isSelected) ? (
            <Box>
              {translations.otherAttachedDeals}
              {currentDeals.map((item: INetHuntDeal) => {
                if (!item || item.id === crmData?.dealId) return null;
                return (
                  <a
                    href={item.netHuntLink}
                    title={translations.openDealInCRM}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    key={item.id}
                  >
                    <Box
                      width="100%"
                      mt={2}
                      borderRadius="5px"
                      position="relative"
                      className={styles.dealBtn}
                    >
                      <Box fontSize="13px">
                        {item.externalName}
                      </Box>

                      {item.responsibleUser && (
                        <Box mt={1} fontSize="11px">
                          {`${translations.responsible}: ${item?.responsibleUser.username}`}
                        </Box>
                      )}
                      {/* {netHuntDealDetails[item.id]?.contact && (
                        <>
                          <Box mt={1} fontSize="11px">
                            {`${translations.client}: ${netHuntDealDetails[item.id]?.contact.name}`}
                          </Box>
                          {netHuntDealDetails[item.id]?.contact.phone && (
                          <Box key={netHuntDealDetails[item.id]?.contact.phone} mt={1} fontSize="11px" display="flex" alignItems="center">
                            <Box fontSize="15px" lineHeight="15px" mr={1}>
                              <PhoneIcon fontSize="inherit" />
                            </Box>
                            {netHuntDealDetails[item.id]?.contact.phone}
                          </Box>
                          )}
                          {netHuntDealDetails[item.id]?.contact.homePhone && (
                          <Box key={netHuntDealDetails[item.id]?.contact.homePhone} mt={1} fontSize="11px" display="flex" alignItems="center">
                            <Box fontSize="15px" lineHeight="15px" mr={1}>
                              <PhoneIcon fontSize="inherit" />
                            </Box>
                            {netHuntDealDetails[item.id]?.contact.homePhone}
                          </Box>
                          )}
                          {netHuntDealDetails[item.id]?.contact.mobilePhone && (
                          <Box key={netHuntDealDetails[item.id]?.contact.mobilePhone} mt={1} fontSize="11px" display="flex" alignItems="center">
                            <Box fontSize="15px" lineHeight="15px" mr={1}>
                              <PhoneIcon fontSize="inherit" />
                            </Box>
                            {netHuntDealDetails[item.id]?.contact.mobilePhone}
                          </Box>
                          )}

                        </>
                        )} */}
                      <Box
                        borderRadius="0 5px 5px 0"
                        position="absolute"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        top={0}
                        right={0}
                        bottom={0}
                        className={`${styles.removeDeal} removeBtn`}
                        title={translations.detachUnitFromDeal}
                        onClick={(e) => handleUnbindDeal(e, item)}
                      >
                        <DeleteForeverIcon />
                      </Box>
                    </Box>
                  </a>
                );
              })}
            </Box>
          ) : null}
        </Box>
      </ThemeProvider>
    </StyledEngineProvider>
  );
});
